import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import stopIcon from '../../img/_Stop.png';
import yieldIcon from '../../img/_Yield.png';
import goIcon from '../../img/_Go.png';
import closeIcon from '../../img/pretty-close-2.png';
import modifiedIcon from './assets/modified.png';
import notModifiedIcon from './assets/not-modified.png';
import ZoneName1 from './ZoneName1';
import ZoneName2 from './ZoneName2';
import DocName1 from './DocName1';
import DocName2 from './DocName2';
import ExpandButton from './ExpandButton';
import NotesButton from './NotesButton';
import MarkForExportButton from './MarkForExportButton';
import RevertButton from './RevertButton';
import LockButton from './LockButton';
import styles from './ExpandedMatchBlockStyles';
import lightPDFIcon from './assets/pdf-light.png';
import darkPDFIcon from './assets/pdf-dark.png';
import NextBestNavigator from './NextBestNavigator';
import lockIcon from './assets/locked.png';
import unlockIcon from './assets/unlock.png';
import searchIcon from './assets/zoom.png'

const parseNumberWithUnit = (val) => {
  if (!val || typeof val !== 'string') return { num: NaN, unit: '' };
  const cleaned = val.replace(/[()]/g, '').replace(/,/g, '').trim();
  const match = cleaned.match(/^(-?[\d.]+)\s*([a-zA-Z%°\/]*)$/);
  if (!match) return { num: NaN, unit: '' };
  let num = parseFloat(match[1]);
  if (val.includes('(') && val.includes(')')) num *= -1;
  return { num, unit: match[2] || '' };
};

const getUserModifiedIcon = (match) => {
  const system = match["System Detected"];
  const user = match["User Modified"];
  const userModified = JSON.stringify(system) !== JSON.stringify(user);
  return (
    <>
      <span style={{ fontSize: '20px', right: '14px', top: '-14px', background: 'none', position: 'absolute', fontStyle: 'italic', float: 'right' }}>
        {userModified ? "Modified" : "Not Modified"}
      </span>
      <img
        title={userModified ? "This match has been modified..." : "This match has not been modified..."}
        src={userModified ? modifiedIcon : notModifiedIcon}
        alt={userModified ? "Modified" : "Not Modified"}
        style={styles.userModifiedIcon}
      />
    </>
  );
};

const headerSynonyms = {
  qty: 'Quantity',
  quantity: 'Quantity',
  quant: 'Quantity',
  total: 'Total',
  acv: 'ACV',
  rcv: 'RCV',
  desc: 'Description',
  description: 'Description',
};

const normalizeHeader = (header) => {
  if (!header) return '';
  const h = header.trim().toLowerCase();
  return headerSynonyms[h] || header.charAt(0).toUpperCase() + header.slice(1);
};

const unifyHeaders = (headers1, headers2) => {
  const h1 = headers1.slice(1).map(normalizeHeader);
  const h2 = headers2.slice(1).map(normalizeHeader);
  const uniqueHeadersSet = new Set([...h1, ...h2]);
  let union = Array.from(uniqueHeadersSet);
  const lastCandidates = ['Total', 'ACV', 'RCV'];
  const lastCol = lastCandidates.find((c) => union.includes(c));
  if (lastCol) {
    union = union.filter((h) => h !== lastCol);
    union.push(lastCol);
  }
  return union;
};

const getValueByHeader = (values, header) => {
  const canonical = headerSynonyms[header.toLowerCase()] || header;
  const keys = Object.keys(values || {});
  const matchKey = keys.find(k => headerSynonyms[k.toLowerCase()] === canonical || k.toLowerCase() === canonical.toLowerCase());
  return matchKey ? values[matchKey] : '-';
};

const ExpandedMatchBlock = ({
  match,
  cleanParsedEstData,
  cleanParsedInsData,
  setCleanParsedEstData,
  setCleanParsedInsData,
  headersOne,
  headersTwo,
  docname1,
  docname2,
  claimId,
  onToggle,
  visibleDocs,
  toggleDoc,
  onSaveMatchUpdate,
  onSaveMatchNotes,
  onSaveMarkForExport,
  onRevertMatch,
  onSaveLockClick,
  lineItemUsageMap
}) => {
  const system = match['User Modified'];
  const doc1Ids = system.doc1.ids || [];
  const doc2Ids = system.doc2.ids || [];
  const designation = system.Designation;
  const unifiedHeaders = useMemo(() => unifyHeaders(headersOne, headersTwo), [headersOne, headersTwo]);

  // Build fast id -> lineItem maps to avoid repeated .find calls
  const estLineItemMap = useMemo(() => {
    const map = {};
    (cleanParsedEstData?.lineItems || []).forEach(obj => {
      const id = Object.keys(obj)[0];
      map[id] = obj[id];
    });
    return map;
  }, [cleanParsedEstData?.lineItems]);

  const insLineItemMap = useMemo(() => {
    const map = {};
    (cleanParsedInsData?.lineItems || []).forEach(obj => {
      const id = Object.keys(obj)[0];
      map[id] = obj[id];
    });
    return map;
  }, [cleanParsedInsData?.lineItems]);

  const [searchText, setSearchText] = useState('');
  const [searchTarget, setSearchTarget] = useState(null);
  const [noteText, setNoteText] = useState(match?.Notes || '');
  const [notesOpen, setNotesOpen] = useState(false);

  const searchInputRefs = useRef({});
  const matchingResultsRef = useRef(null);
  const preventBlurRef = useRef(false);

  const scrollRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    }
  }, [unifiedHeaders]); // run again if headers change
  const scrollAll = (delta) => {
    const elements = document.querySelectorAll('.sync-scroll');
    elements.forEach((el) => {
      el.scrollBy({ left: delta, behavior: 'smooth' });
    });
  };

  const handleNotes = () => setNotesOpen(prev => !prev);

  const getStatusIcon = (match, designation) => {
    const system = match["System Detected"];
    const user = match["User Modified"];
    const userModified = JSON.stringify(system) !== JSON.stringify(user);
  
    if (userModified) {
      const hasNoDoc2 = !user?.doc2?.ids || user.doc2.ids.length === 0;
  
      if (hasNoDoc2) {
        return (
          <img
            title="This is a no-match."
            src={stopIcon}
            alt="No Match"
            style={styles.statusIcon}
          />
        );
      } else {
        return (
          <img
            title="This match was modified by the user."
            src={yieldIcon}
            alt="Similar"
            style={styles.statusIcon}
          />
        );
      }
    }
  
    // If not userModified, render based on designation
    switch (designation) {
      case 'Match':
        return (
          <img
            title="The description and zone are semantically the same."
            src={goIcon}
            alt="Match"
            style={styles.statusIcon}
          />
        );
      case 'Close':
        return (
          <img
            title="The description and zone are semantically the same but numeric values differ."
            src={closeIcon}
            alt="Close"
            style={styles.statusIcon}
          />
        );
      case 'Similar':
        return (
          <img
            title="The description and/or the zone are close but not the same."
            src={yieldIcon}
            alt="Similar"
            style={styles.statusIcon}
          />
        );
      default:
        return (
          <img
            title="There was not a clear match."
            src={stopIcon}
            alt="No Match"
            style={styles.statusIcon}
          />
        );
    }
  };
  
  
  
  const getStatusShadow = () => {
    const system = match["System Detected"];
    const user = match["User Modified"];
    const userModified = JSON.stringify(system) !== JSON.stringify(user);
  
    if (userModified) {
      const hasNoDoc2 = !user?.doc2?.ids || user.doc2.ids.length === 0;
      const hasZeroTotal = crossColumnFinancialDeltas.some(line => line.startsWith('Total - Total:') && line.includes('0.00'));

      if (hasNoDoc2) {
        return '5px 3px 6px rgba(255, 0, 0, 0.3)'; // red (no-match)
      } else if (hasZeroTotal) {
        return '5px 3px 6px rgba(0, 128, 0, 0.3)'; // green (match)
      } else {
        return '5px 3px 6px rgba(255, 215, 0, 0.3)'; // yellow (similar)
      }
    }
  
    // Fallback for unmodified matches
    if (!designation) return '';
    switch (designation) {
      case 'Match':
      case 'Close': // Treat "Close" like "Match"
        return '5px 3px 6px rgba(0, 128, 0, 0.3)';
      case 'Similar':
        return '5px 3px 6px rgba(255, 215, 0, 0.3)';
      default:
        return '5px 3px 6px rgba(255, 0, 0, 0.3)';
    }
  };
  
  
    const usageWarnings1 = useMemo(() => doc1Ids.filter(id => (lineItemUsageMap[id] || 0) > 1), [doc1Ids, lineItemUsageMap]);
  
    const usageWarnings2 = useMemo(() => doc2Ids.filter(id => (lineItemUsageMap[id] || 0) > 1), [doc2Ids, lineItemUsageMap]);
  
    const getDataSet = (isDoc1) => ({
      data: isDoc1 ? cleanParsedEstData : cleanParsedInsData,
      setData: isDoc1 ? setCleanParsedEstData : setCleanParsedInsData,
      ids: isDoc1 ? doc1Ids : doc2Ids,
    });
    const getMatchingResults = useCallback(() => {
      if (!searchTarget || searchText.length < 2) return [];
      const isDoc1 = searchTarget === 'doc1';
      const map = isDoc1 ? estLineItemMap : insLineItemMap;
      const ids = isDoc1 ? doc1Ids : doc2Ids;
      const q = searchText.toLowerCase();
      const out = [];
      for (const [id, entry] of Object.entries(map)) {
        if (out.length >= 20) break;
        if (ids.includes(id)) continue;
        const desc = (entry.values?.DESCRIPTION || '').toLowerCase();
        if (desc.includes(q)) {
          out.push({ id, description: entry.values?.DESCRIPTION || '', userModifiedZone: entry.userModifiedZone || '', locked: entry.locked || false });
        }
      }
      return out;
    }, [searchTarget, searchText, estLineItemMap, insLineItemMap, doc1Ids, doc2Ids]);

    const matchingResults = useMemo(() => getMatchingResults(), [getMatchingResults]);
    
    // Multi-select state for matching results
    const [selectedMatches, setSelectedMatches] = useState(new Set());

    // Clear selection when search target or results change
    useEffect(() => {
      setSelectedMatches(new Set());
    }, [searchTarget, searchText]);

    const toggleSelect = (id) => {
      setSelectedMatches(prev => {
        const s = new Set(prev);
        if (s.has(id)) s.delete(id); else s.add(id);
        return s;
      });
    };

    const selectAllMatches = () => {
      const ids = matchingResults.filter(it => !it.locked).map(it => it.id);
      setSelectedMatches(new Set(ids));
    };

    const clearSelection = () => setSelectedMatches(new Set());

    const addSelectedToMatch = () => {
      if (!searchTarget) return;
      const isDoc1 = searchTarget === 'doc1';
      const { ids } = getDataSet(isDoc1);
      const toAdd = Array.from(selectedMatches).filter(id => !ids.includes(id));
      if (toAdd.length === 0) {
        clearSelection();
        setSearchText('');
        setSearchTarget(null);
        return;
      }
      const newIds = [...ids, ...toAdd];
      const newSystem = isDoc1
        ? { ...system, doc1: { ...system.doc1, ids: newIds } }
        : { ...system, doc2: { ...system.doc2, ids: newIds } };
      onSaveMatchUpdate(newSystem);
      clearSelection();
      setSearchText('');
      setSearchTarget(null);
    };

    const addExistingItemToMatch = useCallback((id) => {
      if (searchTarget !== 'doc1' && searchTarget !== 'doc2') return;
      const isDoc1 = searchTarget === 'doc1';
      const { ids } = getDataSet(isDoc1);
      if (ids.includes(id)) return;
      ids.push(id);
      onSaveMatchUpdate({
        ...system,
        doc1: { ...system.doc1, ids: isDoc1 ? ids : system.doc1.ids },
        doc2: { ...system.doc2, ids: !isDoc1 ? ids : system.doc2.ids },
      });
      setSearchText('');
      setSearchTarget(null);
    }, [searchTarget, onSaveMatchUpdate, system]);
    const removeLineItemFromMatch = (isDoc1, id) => {
      const { ids } = getDataSet(isDoc1);
      const filtered = ids.filter(x => x !== id);
      const newSystem = {
        ...system,
        doc1: { ...system.doc1, ids: isDoc1 ? filtered : system.doc1.ids },
        doc2: { ...system.doc2, ids: !isDoc1 ? filtered : system.doc2.ids },
      };
      onSaveMatchUpdate(newSystem);
    };
    const changeLineItemFromMatch = (oldId, newId) => {
      const updatedDoc2Ids = system.doc2.ids.map(id => id === oldId ? newId : id);
    
      const newSystem = {
        ...system,
        doc2: { ...system.doc2, ids: updatedDoc2Ids },
      };
    
      onSaveMatchUpdate(newSystem);
    };
    
    const footerDeltas = useMemo(() => {
      return unifiedHeaders.map(header => {
        let sum1 = 0, sum2 = 0;
        let unit1Set = new Set(), unit2Set = new Set();
        let valid1 = false, valid2 = false;
        doc1Ids.forEach(id => {
          const item = estLineItemMap[id];
          if (item) {
            const { num, unit } = parseNumberWithUnit(getValueByHeader(item.values || {}, header));
            if (!isNaN(num)) {
              sum1 += num;
              unit1Set.add(unit);
              valid1 = true;
            }
          }
        });
        doc2Ids.forEach(id => {
          const item = insLineItemMap[id];
          if (item) {
            const { num, unit } = parseNumberWithUnit(getValueByHeader(item.values || {}, header));
            if (!isNaN(num)) {
              sum2 += num;
              unit2Set.add(unit);
              valid2 = true;
            }
          }
        });
        const unitsMatch = unit1Set.size <= 1 && unit2Set.size <= 1 &&
          [...unit1Set][0] === [...unit2Set][0];
        const unit = unitsMatch ? [...unit1Set][0] || '' : '';
        if (!valid1 && !valid2) return '-';
        if (!valid1) return `-${sum2.toFixed(2)} ${unit}`.trim();
        if (!valid2) return `${sum1.toFixed(2)} ${unit}`.trim();
        if (!unitsMatch) return '-';
        return `${(sum1 - sum2).toFixed(2)} ${unit}`.trim();
      });
    }, [unifiedHeaders, doc1Ids, doc2Ids, estLineItemMap, insLineItemMap]);
    const crossColumnFinancialDeltas = useMemo(() => {
      const headers = ['ACV', 'RCV', 'Total'];
      const results = [];
      for (let h1 of headers) {
        let sum1 = 0, unit1Set = new Set(), valid1 = false;
        doc1Ids.forEach(id => {
          const item = estLineItemMap[id];
          if (item) {
            const val = getValueByHeader(item.values || {}, h1);
            const { num, unit } = parseNumberWithUnit(val);
            if (!isNaN(num)) {
              sum1 += num;
              unit1Set.add(unit);
              valid1 = true;
            }
          }
        });
        for (let h2 of headers) {
          let sum2 = 0, unit2Set = new Set(), valid2 = false;
          doc2Ids.forEach(id => {
            const item = insLineItemMap[id];
            if (item) {
              const val = getValueByHeader(item.values || {}, h2);
              const { num, unit } = parseNumberWithUnit(val);
              if (!isNaN(num)) {
                sum2 += num;
                unit2Set.add(unit);
                valid2 = true;
              }
            }
          });
          const unitsMatch = unit1Set.size <= 1 && unit2Set.size <= 1 &&
            [...unit1Set][0] === [...unit2Set][0];
          const unit = unitsMatch ? [...unit1Set][0] || '' : '';
          if (valid1 && valid2 && unitsMatch) {
            const diff = (sum1 - sum2).toFixed(2);
            results.push(`${h1} - ${h2}: ${diff} ${unit}`.trim());
          }
        }
      }
      return results;
    }, [doc1Ids, doc2Ids, estLineItemMap, insLineItemMap]);
    const handleLockClick = (itemId) => {
      if (onSaveLockClick) onSaveLockClick(itemId);
    };  
  
    const saveNote = () => {
      if (onSaveMatchNotes) onSaveMatchNotes(noteText);
    };

    // Memoized UI elements to avoid re-creating JSX each render
    const statusIconElem = useMemo(() => getStatusIcon(match, designation), [match, designation]);
    const statusShadowVal = useMemo(() => getStatusShadow(), [match, designation, crossColumnFinancialDeltas]);
    const getSubtotals = (ids, map) => {
      return unifiedHeaders.map(header => {
        let sum = 0;
        let unitSet = new Set();
        let valid = false;
        ids.forEach(id => {
          const item = map[id];
          if (item) {
            const { num, unit } = parseNumberWithUnit(getValueByHeader(item.values || {}, header));
            if (!isNaN(num)) {
              sum += num;
              unitSet.add(unit);
              valid = true;
            }
          }
        });
        const unit = unitSet.size === 1 ? [...unitSet][0] : '';
        return valid ? `${sum.toFixed(2)} ${unit}`.trim() : '-';
      });
    };  
    const subtotalsDoc1 = useMemo(() => getSubtotals(doc1Ids, estLineItemMap), [doc1Ids, estLineItemMap, unifiedHeaders]);
    const subtotalsDoc2 = useMemo(() => getSubtotals(doc2Ids, insLineItemMap), [doc2Ids, insLineItemMap, unifiedHeaders]);
    const renderDocBlock = (ids, cleanData, isDoc1, docName, ZoneNameComp, toggleKey) => {
      const target = isDoc1 ? 'doc1' : 'doc2';
      const currentWarnings = isDoc1 ? usageWarnings1 : usageWarnings2;
      const map = isDoc1 ? estLineItemMap : insLineItemMap;
      const subtotals = isDoc1 ? subtotalsDoc1 : subtotalsDoc2;
    
      return (
        <div style={{ opacity: ((isDoc1 && searchTarget === "doc2") || (!isDoc1 && searchTarget === "doc1")) ? '25%' : '100%', ...styles.docBlock, ...(isDoc1 ? styles.doc1 : styles.doc2) }}>
          <div style={isDoc1 ? styles.li1InfoContainer : styles.li2InfoContainer}>
            <div style={styles.tableContainer}>
            {(() => {
              const allowedIds = system["Allowed Ids"] || [];
                      const filteredIds = (ids.length === 0 && !isDoc1 ? ['placeholder-doc2'] : ids).filter((id) => {
                if (id === 'placeholder-doc2') return true;
                const item = map[id];
                if (!item) return true;
                // Filter out locked items unless in allowedIds
                if (item.locked && !allowedIds.includes(id)) return false;
                return true;
              });
  
              return filteredIds.map((id, index) => {
                const item = map[id];
                const isPlaceholder = id === 'placeholder-doc2' || !item;
                const matchId = isPlaceholder ? `placeholder-${index}` : id;
                const lineItem = isPlaceholder ? {} : item || {};
                const values = lineItem.values || {};
                const desc = isPlaceholder ? '[no match]' : getValueByHeader(values, 'description');
                const onlyOneLineItem = ids.length === 1;
                return (
                  <div
                    key={id}
                    style={{
                      display: 'flex',
                      backgroundColor: isDoc1 ? '#49a7ab' : '#f2f2f2',
                      position: 'relative',
                      overflow: 'visible'
                    }}
                  >
  
                    {(system?.doc2?.ids?.length === 1) && (
                      <NextBestNavigator
                        isDoc1={isDoc1}
                        nextBest={(system?.nextBest?.ids || []).filter(id => id?.trim() !== '')}
                        currentId={system.doc2.ids[0]}
                        changeLineItemFromMatch={changeLineItemFromMatch}
                      />
                    )}
  
                    <div
                      style={{
                        width: '15%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        padding: '16px',
                        backgroundColor: isDoc1 ? '#42999d' : '#e5e9e8',
                        boxSizing: 'border-box'
                      }}
                    >
                      {isDoc1
                        ? <DocName1 docname1={docName} claimId={claimId} />
                        : <DocName2 docname2={docName} claimId={claimId} />}
                      <ZoneNameComp
                        li={isPlaceholder ? null : { [matchId]: lineItem }}
                        cleanParsedEstData={isDoc1 ? cleanParsedEstData : null}
                        setCleanParsedEstData={isDoc1 ? setCleanParsedEstData : null}
                        cleanParsedInsData={!isDoc1 ? cleanParsedInsData : null}
                        setCleanParsedInsData={!isDoc1 ? setCleanParsedInsData : null}
                        claimId={claimId}
                      />
                      <button
                        style={isDoc1 ? styles.showRefDoc1 : styles.showRefDoc2}
                        onClick={() => toggleDoc(toggleKey)}
                        title="Open the file."
                      >
                        <img
                          src={isDoc1 ? lightPDFIcon : darkPDFIcon}
                          alt="doc icon"
                          style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }}
                        />
                        {visibleDocs?.[toggleKey] ? 'Hide Reference File' : 'View Reference File'}
                      </button>
                    </div>
  
                    <div
                      id={matchId}
                      style={{
                        width: '70%',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '16px',
                        boxSizing: 'border-box',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {currentWarnings.includes(matchId) && (
                        <div style={{ color: 'orange', fontWeight: 'bold' }}>
                          Warning: This LINE ITEM is used in multiple MATCHES
                        </div>
                      )}
  
                      <div style={styles.description}>
                        <p style={{ color: isDoc1 ? '#425158' : '#839299', margin: '0', padding: '0' }}>
                          DESCRIPTION:&nbsp;<strong style={{ color: isDoc1 ? '#ffffff' : '#425158', fontSize: '18px' }}>{desc}</strong>
                        </p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between' }}>
                        {/* Scrollable block with all but the last header */}
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', maxWidth: '80%' }}>
                          {/* Left Triangle */}
                          {hasOverflow && (
                            <div style={{ left: -10 }} onClick={() => scrollAll(-155)}>
                              <div style={styles.triangleLeft}></div>
                            </div>
                          )}

                          {/* Scrollable Content */}
                          <div
                            className="sync-scroll"
                            ref={scrollRef}
                            style={{
                              overflowX: 'hidden',
                              flex: 1,
                              display: 'flex',
                              ...styles.scrollableBlock,
                            }}
                          >
                            <div style={{ ...styles.carouselRow, display: 'flex' }}>
                              {unifiedHeaders.slice(0, -1).map((header, i) => (
                                <div
                                  key={i}
                                  style={{
                                    ...styles.headerCell,
                                    ...(isDoc1
                                      ? (i % 2 === 0 ? styles.evenCell1 : styles.oddCell1)
                                      : (i % 2 === 0 ? styles.evenCell2 : styles.oddCell2)),
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    padding: '4px 8px',
                                    minWidth: '100px',
                                    textAlign: 'center',
                                    fontSize: '16px'
                                  }}
                                >
                                  <span style={{ fontWeight: 500, color: isDoc1 ? '#3f5a61' : '#8b9397' }}>{header}</span>
                                  <div style={{height: '8px'}}></div>
                                  <span style={{ color: isDoc1 ? '#ffffff' : '#435652' }}>{getValueByHeader(values, header)}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Right Triangle */}
                          {hasOverflow && (
                            <div style={{ right: -10 }} onClick={() => scrollAll(155)}>
                              <div style={styles.triangleRight}></div>
                            </div>
                          )}
                        </div>

                        {/* Last value on the right side */}
                        <div style={{
                          flexShrink: 0,
                          //padding: '0 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          boxSizing: 'border-box',
                          minWidth: '120px'
                        }}>
                          <div style={{
                            color: isDoc1 ? '#425158' : '#839299',
                            fontSize: '1.2em',
                            fontWeight: 'bold',
                            marginBottom: '4px',
                          }}>
                            {unifiedHeaders[unifiedHeaders.length - 1]}
                          </div>
                          <div style={{
                            fontSize: '1.5em',
                            fontWeight: 600,
                            color: isDoc1 ? 'white' : '#425158',
                          }}>
                            {getValueByHeader(values, unifiedHeaders[unifiedHeaders.length - 1])}
                          </div>
                        </div>
                      </div>
                    </div>
  
                    <div
                      style={{
                        width: '15%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        padding: '16px',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        justifyContent: 'flex-start'
                      }}
                    >
  
                      {lineItem && Object.keys(lineItem).length > 0 && (
                        <LockButton
                          onClick={() => handleLockClick(id)}
                          isLocked={lineItem.locked}
                          isDoc1={isDoc1}
                        />
                      )}
  
                      <button
                        style={isDoc1 ? styles.addBtn1 : styles.addBtn2}
                        title="Add a line item to this match."
                        onClick={() => {
                          setSearchText('');
                          setSearchTarget(isDoc1 ? 'doc1' : 'doc2');
                        
                          setTimeout(() => {
                            searchInputRefs.current[match.id]?.focus();
                          }, 0);
                        }}
                        
                      >
                        ADD
                      </button>
                      <button
                        style={{
                          ...(isDoc1 ? styles.removeBtn1 : styles.removeBtn2),
                          ...((isDoc1 && (index === 0 || doc2Ids.length === 0)) || isPlaceholder
                            ? { opacity: 0.4, pointerEvents: 'none', cursor: 'default' }
                            : {}),
                        }}
                        title="Remove this line item from this match."
                        disabled={isPlaceholder}
                        onClick={() => {
                          if (!isPlaceholder && !((isDoc1 && (index === 0 || doc2Ids.length === 0)))) {
                            removeLineItemFromMatch(isDoc1, id);
                          }
                        }}
                      >
                        REMOVE
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
  
              
            </div>
            
            <div style={{ display: 'flex', position: 'relative', flex: 1, width: '100%', boxSizing: 'border-box', alignItems: 'center'}}>
            <div style={{ width: '15%'}}></div>
              {ids.length > 1 && (
                <div style={{ display: 'flex', width: '70%', justifyContent: 'space-between', padding: hasOverflow ? '0 32px' : '0 16px', boxSizing: 'border-box'}}>
                  {/* Scrollable subtotal block */}
                  <div
                    className="sync-scroll"
                    ref={scrollRef}
                    style={{ ...styles.scrollableBlock, maxWidth: '80%', overflow: 'hidden' }}
                  >
                    <div style={styles.subtotalRow}>
                      {subtotals.slice(0, -1).map((subtotal, i) => {
                        const isEven = i % 2 === 0;
                        const bgStyle = isDoc1
                          ? (isEven ? styles.evenCell1 : styles.oddCell1)
                          : (isEven ? styles.evenCell2 : styles.oddCell2);

                        const headerColor = isDoc1 ? '#3f5a61' : '#8b9397';
                        const valueColor = isDoc1 ? 'white' : '#435652';

                          return (
                          <div
                            key={i}
                            style={{
                              ...styles.headerCell,
                              minWidth: '100px',
                              ...bgStyle,
                            }}
                          >
                            <div style={{ textAlign: 'center', fontSize: '16px', color: headerColor }}>∑{unifiedHeaders[i]}</div>
                            <hr />
                            <div style={{ textAlign: 'center', fontSize: '16px', color: valueColor }}>{subtotal}</div>
                          </div>
                        );
                      })}
                    </div>

                  </div>

                    <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      padding: '12px 0px',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: isDoc1 ? 'white' : '#839299',
                      minWidth: '140px',
                      marginRight: hasOverflow ? '-34px' : '-18px',
                      boxSizing: 'border-box',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ padding: '0'}}>{subtotals.slice(-1)[0]}</span>
                  </div>
                </div>
              )}
              
            </div>
          </div>
        </div>
      );
    };
  
  return (
      <div style={styles.matchBlock}>
        <div style={styles.matchHeader}>{statusIconElem}</div>
        <div>{getUserModifiedIcon(match)}</div>
        <div
          style={{
            ...styles.matchDetails,
            boxShadow: statusShadowVal, // unified match-based shadow
            borderRadius: '6px', // optional for softer edges
          }}
        >
          {/* doc1 scrollable block */}
          {renderDocBlock(
            doc1Ids,
            cleanParsedEstData,
            true,
            docname1,
            ZoneName1,
            'doc1'
          )}

          {/* doc2 scrollable block */}
          {renderDocBlock(
            doc2Ids,
            cleanParsedInsData,
            false,
            docname2,
            ZoneName2,
            'doc2'
          )}
        </div>
        <div style={styles.matchBlockFoooter}>
          <div style={styles.searchContainer}>
            <div style={styles.searchBox}>
              <input
                ref={el => { if (el) searchInputRefs.current[match.id] = el; }}
                title="Click 'ADD' to search for a line item"
                disabled={(searchTarget === null)}
                style={{...styles.searchInput, opacity: (searchTarget === null) ? "25%" : "100%"}}
                value={searchText}
                onBlur={() => {
                  setTimeout(() => {
                    // If the user is interacting with the dropdown (mouse down), don't close it
                    if (preventBlurRef.current) return;
                    const active = document.activeElement;
                    if (matchingResultsRef.current && matchingResultsRef.current.contains(active)) return;
                    setSearchTarget(null);
                    setSearchText('');
                  }, 100);
                }}
                onChange={e => setSearchText(e.target.value)}
                autoFocus
              />
              <img style={styles.searchIcon} src={searchIcon} alt="Search" />
            </div>
            <div
              ref={matchingResultsRef}
              onMouseDown={() => { preventBlurRef.current = true; }}
              onMouseUp={() => { setTimeout(() => { preventBlurRef.current = false; }, 0); }}
              style={{position: 'absolute', marginTop: '2px', marginLeft: '2px', zIndex: '9999', maxHeight: '150px', overflowY: 'scroll'}}>
              {/* Minimal header: Add button only (show only when dropdown is active and has results) */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderBottom: '1px solid #eee', background: '#fff' }}>
                {/* Add button on the left */}
                <div>
                  {(searchTarget && searchText.length >= 2 && matchingResults.length > 0) && (
                    <button
                      onClick={addSelectedToMatch}
                      disabled={!selectedMatches || selectedMatches.size === 0 || !searchTarget}
                      style={{ padding: '6px 8px', fontSize: 12 }}
                    >
                      Add
                    </button>
                  )}
                </div>

                {/* Select All on the right */}
                <div>
                  {(searchTarget && searchText.length >= 2 && matchingResults.length > 0) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#425158', fontSize: 13 }}>
                      <span style={{ marginLeft: 4 }}>SELECT ALL</span>
                      <input
                        type="checkbox"
                        aria-label="Select all matching results"
                        checked={matchingResults.length > 0 && selectedMatches && selectedMatches.size === matchingResults.filter(it => !it.locked).length}
                        onChange={(e) => { if (e.target.checked) selectAllMatches(); else clearSelection(); }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {matchingResults.map(item => {
                      const { id, description, userModifiedZone, locked } = item;
                      const isSelected = selectedMatches && selectedMatches.has(id);

                      return (
                        <div
                          key={id}
                          style={{ 
                            ...styles.resultBtn, 
                            pointerEvents: locked ? 'none' : 'auto',
                            opacity: locked ? 0.5 : 1
                          }}
                          onClick={() => { if (!locked) addExistingItemToMatch(id); }}
                          tabIndex={0}
                          role="button"
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!locked) addExistingItemToMatch(id);
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center'}}>
                            <img
                              src={locked ? lockIcon : unlockIcon}
                              alt={locked ? 'Locked' : 'Unlocked'}
                              style={{width: '12px', height: 'auto', marginRight: '8px'}}
                            />
                            <strong>{description || '[no description]'}</strong>
                            <div style={{ marginLeft: 'auto' }}>
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onClick={(e) => { e.stopPropagation(); }}
                                onChange={(e) => { e.stopPropagation(); if (!locked) toggleSelect(id); }}
                                disabled={locked}
                                aria-label={`Select ${description || id}`}
                              />
                            </div>
                          </div>
                          <div style={{ fontSize: '0.85em', color: '#777', marginLeft: '24px' }}>
                            Zone: {userModifiedZone || 'unknown'}
                          </div>
                        </div>
                      );
                    })}
                    {searchText.length >= 2 && matchingResults.length === 0 && (
                      <div style={{ fontStyle: 'italic', marginTop: 4 }}>No results found</div>
                    )}
            </div>
          </div>
          
          <div style={styles.deltasContainer}>
            <div style={{ ...styles.docBlock, ...styles.doc2, display: 'flex', padding: 0, marginTop: 8 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  flex: '1 1 0%',
                  overflow: 'hidden',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    position: 'relative',
                    flex: 1,
                    maxWidth: '80%',
                    boxSizing: 'border-box',
                    alignItems: 'center',
                  }}
                >
                  {/* Left Arrow */}
                  {hasOverflow && (
                    <div style={{ left: -10 }} onClick={() => scrollAll(-155)}>
                      <div style={styles.triangleLeft}></div>
                    </div>
                  )}
                  {/* Scrollable Deltas */}
                  <div
                    className="sync-scroll"
                    style={{ ...styles.scrollableBlock, overflowX: 'hidden', flex: '1 1 0%' }}
                  >
                    <div style={styles.carouselRow}>
                      {unifiedHeaders.slice(0, -1).map((header, i) => {
                        const rawValue = footerDeltas[i];
                        // Parse numeric value by stripping out non-numeric chars except '.' and '-'
                        const numericValue = parseFloat(String(rawValue).replace(/[^0-9.-]/g, ''));

                        return (
                          <div
                            key={i}
                            style={{
                              ...styles.headerCell,
                              ...(i % 2 === 0 ? styles.evenCell2 : styles.oddCell2),
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              padding: '4px 8px',
                              minWidth: '100px',
                              textAlign: 'center',
                              fontSize: '16px',
                            }}
                          >
                            <span style={{ fontWeight: 500, color: '#8b9397' }}>&Delta;{header}</span>
                            <div style={{height: '8px'}}></div>
                            <span style={{ color: '#435652' }}>{rawValue}</span>                          
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Arrow */}
                  {hasOverflow && (
                    <div style={{ right: -10 }} onClick={() => scrollAll(155)}>
                      <div style={styles.triangleRight}></div>
                    </div>
                  )}
                </div>

                {/* Final Non-Scrollable Delta */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: '12px 16px',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#839299',
                    minWidth: '140px',
                    boxSizing: 'border-box',
                    textAlign: 'left',
                  }}
                >
                <span
                  style={{
                    padding: '0px',
                    marginTop: '4px',
                    color: footerDeltas[unifiedHeaders.length - 1] > 0 ? 'red' : 'green',
                  }}
                >
                  {Math.abs(footerDeltas[unifiedHeaders.length - 1]).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                  })}
                </span>
                </div>
              </div>

              </div>
            <div style={styles.crossColumnMath}>
            {crossColumnFinancialDeltas.map((line, i) => {
              const [label, value] = line.split(/:(.+)/); // split on first colon only
              const numericValue = parseFloat(value.replace(/[^0-9.-]/g, '')); // strip non-numeric chars

              // Transform "RCV - Total" → "∆(RCV, Total)"
              const transformedLabel = label.replace(/(.+?) - (.+)/, '∆($1, $2)');

              // Always display the absolute value as a dollar amount
              const displayValue = Math.abs(numericValue).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
              });

              return (
                <span key={i} style={{ fontSize: 17 }}>
                  <span
                    style={{
                      color: '#425158',
                      padding: '0px 2px',
                      fontWeight: 'normal',
                    }}
                  >
                    {transformedLabel}:
                  </span>{' '}
                  <span
                    style={{
                      color: numericValue > 0 ? 'red' : 'green',
                      padding: '0px 2px',
                      fontWeight: '900',
                    }}
                  >
                    {displayValue}
                  </span>
                </span>
              );
            })}
            </div>
          </div>
          <div style={styles.buttonsContainer}>
            <MarkForExportButton
              checked={!!match["Mark For Export"]}
              onChange={(checked) => onSaveMarkForExport && onSaveMarkForExport(checked)}
            />
            <NotesButton notesOpen={notesOpen} onClick={handleNotes} noteText={noteText} />
            <RevertButton
              match={match}
              setCleanParsedEstData={setCleanParsedEstData}
              setCleanParsedInsData={setCleanParsedInsData}
              onClick={(match) => onRevertMatch && onRevertMatch(match)}
            />
            <ExpandButton expanded={match.expanded} onClick={onToggle} />
          </div>
        </div>
        {(notesOpen || noteText) && (
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: '-64px', position: 'relative', maxWidth: '70%' }}>
            {(noteText && !notesOpen)&& (
              <span style={{ fontSize: 12, fontWeight: 'bold', padding: 0 }}>
                Notes:
              </span>
            )}
            <textarea
              rows={2}
              value={noteText}
              onClick={() => setNotesOpen(true)}
              onChange={e => setNoteText(e.target.value)}
              onBlur={saveNote}
              placeholder="Add a note for this match..."
              style={{
                width: '25%',
                padding: 8,
                fontSize: 12,
                border: notesOpen ? '1px solid grey' : 'none',
                boxSizing: 'border-box',
                resize: 'none',
              }}
            />
            {/* Close Button */}
            {notesOpen && (
              <button
                onClick={() => setNotesOpen(false)}
                style={{
                  position: 'absolute',
                  borderRadius: '0px',
                  border: 'none',
                  height: 'auto',
                  backgroundColor: '#489EA3',
                  color: 'white',
                  width: '48px',
                  marginLeft: '-48px',
                  left: '25%',
                  fontSize: 12,
                  padding: '4px 6px',
                  cursor: 'pointer',
                }}
              >
                SAVE
              </button>
            )}
          </div>
        )}
        
      </div>
  );
};

export default React.memo(ExpandedMatchBlock);
